//#region Shared - Data - Debtors
/**
 * ### PATH_SHARED_STORE_DATA_DEBTORS_GET
 * Constructs the Firestore path to access the debtors collection for a given store.
 *
 * @function PATH_SHARED_STORE_DATA_DEBTORS_GET
 * @param {string} storeId - The unique identifier of the store.
 * @returns {string} Firestore path to the debtors' collection.
 *
 * @directory
 * ```text
 * shared
 * └── stores_data
 *     └── ${storeId}
 *         └── data
 *             └── debtors
 * ```
 *
 * @example
 * ```ts
 * const path = PATH_SHARED_STORE_DATA_DEBTORS_GET('store-123');
 * // "shared/stores_data/store-123/data/debtors"
 * ```
 */
export const PATH_SHARED_STORE_DATA_DEBTORS_GET = (storeId: string): string => {
  return `shared/stores_data/${storeId}/data/debtors`;
};

/**
 * ### PATH_SHARED_STORE_DATA_DEBTORS_DEBTOR_INFO
 * Constructs the Firestore path to access a specific debtor's info within a given store.
 *
 * @function PATH_SHARED_STORE_DATA_DEBTORS_DEBTOR_INFO
 * @param {string} storeId - The unique identifier of the store.
 * @param {string} debtorId - The unique identifier of the debtor.
 * @returns {string} Firestore path to the debtor document.
 *
 * @directory
 * ```text
 * shared
 * └── stores_data
 *     └── ${storeId}
 *         └── data
 *             └── debtors
 *                 └── ${debtorId}
 * ```
 *
 * @example
 * ```ts
 * const path = PATH_SHARED_STORE_DATA_DEBTORS_DEBTOR_INFO('store-123', 'debtor-789');
 * // "shared/stores_data/store-123/data/debtors/debtor-789"
 * ```
 */
export const PATH_SHARED_STORE_DATA_DEBTORS_DEBTOR_INFO = (storeId: string, debtorId: string): string => {
  return `shared/stores_data/${storeId}/data/debtors/${debtorId}`;
};

/**
 * ### PATH_SHARED_STORE_DATA_DEBTORS_CONTACTS_GET
 * Constructs the Firestore path to the debtors contacts collection for a given store.
 */
export const PATH_SHARED_STORE_DATA_DEBTORS_CONTACTS_GET = (storeId: string): string => {
  return `shared/stores_data/${storeId}/data/debtors_contacts`;
};
//#endregion

//#region Shared - Data - Transactions Verify
/**
 * ### PATH_SHARED_STORE_DATA_TRANSACTIONS_VERIFY
 * Constructs the Firestore path to access transaction verification data for a given store and table.
 *
 * @function PATH_SHARED_STORE_DATA_TRANSACTIONS_VERIFY
 * @param {string} storeId - The unique identifier of the store.
 * @param {'stochist' | 'stoctran'} table - The transaction table name.
 * @returns {string} Firestore path to the transaction verify document.
 *
 * @directory
 * ```text
 * shared
 * └── stores_data
 *     └── ${storeId}
 *         └── data
 *             └── transactions_verify
 *                 └── ${table}
 * ```
 *
 * @example
 * ```ts
 * const path = PATH_SHARED_STORE_DATA_TRANSACTIONS_VERIFY('store-123', 'stochist');
 * // "shared/stores_data/store-123/data/transactions_verify/stochist"
 * ```
 */
export const PATH_SHARED_STORE_DATA_TRANSACTIONS_VERIFY = (
  storeId: string,
  table: 'stochist' | 'stoctran',
): string => {
  return `shared/stores_data/${storeId}/data/transactions_verify/${table}`;
};
//#endregion

//#region FACTORY - SHARED PATHS

/**
 * Factory for generating Firestore shared data paths in a store-centric way.
 *
 * Use this factory to generate paths under `/shared/stores_data/{storeId}/...`.
 * You must first call `setStoreId(storeId)` to set the current store before using
 * methods like `stock()`, `suppliers()`, `lineColours()`, etc.
 */
export const createSharedPaths = (): {
  barcodeScannerUsage(): string;
  base: string;
  currency(): string;
  customPath(...segments: string[]): string;
  dataTimestamps(): string;
  debtorInfo(debtorId: string): string;
  debtors(): string;
  debtorsContacts(): string;
  departments(): string;
  lastUpdatedStockTags(): string;
  lineColours(): string;
  messagesFromApp(): string;
  setStoreId(store: string): ReturnType<typeof createSharedPaths>;
  singularDoc(docName: string): string;
  singularDocs(): string;
  stock(): string;
  stockItem(code: string): string;
  storeInfo(): string;
  storeSettings(): string;
  stores(): string;
  subDepartments(): string;
  supplierInfo(supplierId: string): string;
  suppliers(): string;
  vatRates(): string;
  warehouses(): string;
} => {
  let storeId: string | null = null;

  return {
    /** Base path for all shared data */
    base: 'shared',

    /** Generic method to build any path under shared for this store */
    customPath(...segments: string[]): string {
      if (!storeId) throw new Error('Store ID not set');
      return `${this.stores()}/${storeId}/${segments.join('/')}`;
    },

    // ────────────────────────────────
    // Singular documents
    // ────────────────────────────────

    /** Path to currency singular document for the current store */
    currency(): string {
      return this.singularDoc('currency');
    },

    /** Path to data_timestamps singular document for the current store */
    dataTimestamps(): string {
      return this.singularDoc('data_timestamps');
    },

    /** Path to departments singular document for the current store */
    departments(): string {
      return this.singularDoc('departments');
    },

    /** Path to line_colours singular document for the current store */
    lineColours(): string {
      return this.singularDoc('line_colours');
    },

    /** Path to store_info singular document for the current store */
    storeInfo(): string {
      return this.singularDoc('store_info');
    },

    /** Path to store_settings singular document for the current store */
    storeSettings(): string {
      return this.singularDoc('store_settings');
    },

    /** Path to sub_departments singular document for the current store */
    subDepartments(): string {
      return this.singularDoc('sub_departments');
    },

    /** Path to vat_rates singular document for the current store */
    vatRates(): string {
      return this.singularDoc('vat_rates');
    },

    /** Path to warehouses singular document for the current store */
    warehouses(): string {
      return this.singularDoc('warehouses');
    },

    // ────────────────────────────────
    // Other docs/collections
    // ────────────────────────────────

    /** Path to the current barcode scanner usage document for the store */
    barcodeScannerUsage(): string {
      if (!storeId) throw new Error('Store ID not set');
      return `${this.stores()}/${storeId}/data/barcode_scanner_usage/current`;
    },

    /** Path to last updated stock tags for the current store */
    lastUpdatedStockTags(): string {
      if (!storeId) throw new Error('Store ID not set');
      return `${this.stores()}/${storeId}/data/last_updates/stock/tags`;
    },

    /** Path to messages from app last seen for the current store */
    messagesFromApp(): string {
      if (!storeId) throw new Error('Store ID not set');
      return `${this.stores()}/${storeId}/messages/from_app/last_seen`;
    },

    /** Path to a specific singular document for the current store */
    singularDoc(docName: string): string {
      return `${this.singularDocs()}/${docName}`;
    },

    /** Path for singular documents of the current store */
    singularDocs(): string {
      if (!storeId) throw new Error('Store ID not set');
      return `${this.stores()}/${storeId}/data/singular_documents`;
    },

    /** Path to stock collection for the current store */
    stock(): string {
      if (!storeId) throw new Error('Store ID not set');
      return `${this.stores()}/${storeId}/data/stock`;
    },

    /** Path to a specific stock item by code for the current store */
    stockItem(code: string): string {
      return `${this.stock()}/${code}`;
    },

    /** Path segment for all stores */
    stores(): string {
      return `${this.base}/stores_data`;
    },

    /** Path to suppliers collection for the current store */
    suppliers(): string {
      if (!storeId) throw new Error('Store ID not set');
      return `${this.stores()}/${storeId}/data/suppliers`;
    },

    /** Path to debtors collection for the current store */
    debtors(): string {
      if (!storeId) throw new Error('Store ID not set');
      return `${this.stores()}/${storeId}/data/debtors`;
    },

    /** Path to debtors contacts collection for the current store */
    debtorsContacts(): string {
      if (!storeId) throw new Error('Store ID not set');
      return `${this.stores()}/${storeId}/data/debtors_contacts`;
    },

    /** Path to a specific supplier document for the current store */
    supplierInfo(supplierId: string): string {
      return `${this.suppliers()}/${supplierId}`;
    },

    /** Path to a specific debtor document for the current store */
    debtorInfo(debtorId: string): string {
      return `${this.debtors()}/${debtorId}`;
    },

    // ────────────────────────────────
    // Store context
    // ────────────────────────────────

    /** Set the current store ID for chaining */
    setStoreId(store: string): ReturnType<typeof createSharedPaths> {
      storeId = store;
      return this;
    },
  };
};

//#endregion
